/** @import {AuthContext, GetServerSideAuthProps} from './utils' */

import { Callout, Flex, Separator, Text, TextField } from '@radix-ui/themes'
import { pack } from '@synqly/fun'
import { signIn as authenticate } from 'next-auth/react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { Title as DialogTitle } from '../../components/dialog'
import * as Form from '../../components/form'
import { Link } from '../../components/link'
import { useState } from 'react'
import { useLocalStorage } from '../../hooks/use-storage'
import { SemanticColors } from '../../styles/colors'
import * as Disclosure from '../../components/disclosure'
import { Actions, notFound } from './utils'
import { EmailValidation, PasswordValidation } from './validation'
import { callAPI } from '@synqly/sdk/server'
import { withSecret } from '../server-side-secrets'

export { SignInPage as Page, getServerSideProps, routeParams }

const routeParams = '[organizationId]'

/**
 * @param {AuthContext & {
 *   organization?: {
 *     id: string
 *     name: string
 *     fullname: string
 *   }
 *   ssoProviders?: Record<
 *     string,
 *     {
 *       id: string
 *       fullname: string
 *     }
 *   >
 *   query: {
 *     callbackUrl: string
 *     error: string
 *     message: string
 *     email: string
 *     organizationId: string
 *   }
 *   redirectUrl: string
 *   validation: {
 *     [field: string]: {
 *       rules?: {
 *         pattern: string
 *       }
 *       messages: JSX.Element[]
 *     }
 *   }
 * }} props
 */
function SignInPage({
  query,
  error,
  routes,
  callbackUrl,
  organization,
  ssoProviders,
}) {
  if (organization) {
    return (
      <OrganizationSignInPage
        callbackUrl={callbackUrl}
        error={error}
        organization={organization}
        query={query}
        routes={routes}
        ssoProviders={ssoProviders}
      />
    )
  }

  return (
    <SelectOrganizationPage
      callbackUrl={callbackUrl}
      error={error}
      routes={routes}
    />
  )
}

function SelectOrganizationPage({ error, routes, callbackUrl }) {
  const router = useRouter()
  const [orgId] = useLocalStorage('/auth:{orgId}')

  return (
    <>
      <Head>
        <title>Sign in</title>
      </Head>

      <DialogTitle>Sign In</DialogTitle>

      <Form.Root action={validateOrganization}>
        <Disclosure.Root open={!!error}>
          <Disclosure.Content>
            <Callout.Root
              color={
                SemanticColors[
                  Messages[error?.message]?.severity ?? failedLogon.severity
                ]
              }
            >
              <Callout.Text>
                {Messages[error?.message]?.message ?? failedLogon.message}
              </Callout.Text>
            </Callout.Root>
          </Disclosure.Content>
        </Disclosure.Root>
        <Form.Field name="organizationId" required>
          <Form.Label>
            <Text>Organization</Text>
          </Form.Label>
          <Form.Control asChild>
            <TextField.Root
              autoComplete="off"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- not applicable
              autoFocus
              defaultValue={orgId}
              placeholder="acme"
              type="text"
            />
          </Form.Control>
        </Form.Field>

        <Form.Submit>Continue</Form.Submit>

        {!!routes[Actions.SignUp] && (
          <Text align="center" mt="2" size="1">
            Not registered?{' '}
            <Link href={routes[Actions.SignUp]}>Create account</Link>
          </Text>
        )}
      </Form.Root>
    </>
  )

  /** @param {FormData} formData */
  async function validateOrganization(formData) {
    const target = {
      pathname: routes[Actions.SignIn],
      query: pack({
        callbackUrl,
        organizationId: formData.get('organizationId'),
      }),
    }

    await router.push(target)
  }
}

/**
 * @param {{
 *   organization?: {
 *     id: string
 *     name: string
 *     fullname: string
 *   }
 *   ssoProviders?: Record<
 *     string,
 *     {
 *       id: string
 *       fullname: string
 *     }
 *   >
 *   query: {
 *     callbackUrl: string
 *     error: string
 *     message: string
 *     name: string
 *   }
 *   routes: {
 *     signIn: string
 *     signUp: string | false
 *     reset: string | false
 *     singleSignOn: string | false
 *   }
 *   redirectUrl: string
 *   validation: {
 *     [field: string]: {
 *       rules?: {
 *         pattern: string
 *       }
 *       messages: JSX.Element[]
 *     }
 *   }
 * }} props
 */
function OrganizationSignInPage({
  error,
  organization,
  query: { callbackUrl, name: initialEmail },
  routes,
  ssoProviders,
}) {
  const router = useRouter()
  const [email, setEmail] = useState(initialEmail ?? '')

  const ssoProvidersList = Object.values(ssoProviders ?? {})

  return (
    <>
      <Head>
        <title>Sign in</title>
      </Head>

      <DialogTitle>{organization.fullname}</DialogTitle>
      <Form.Root
        action={builtinSignIn}
        initialMessage={error ? Messages[error?.message] ?? failedLogon : null}
      >
        <Form.Field hidden name="organizationId" required>
          <Form.Control asChild>
            <input type="hidden" value={organization.name} />
          </Form.Control>
        </Form.Field>

        <Form.Field name="username" required>
          <Form.Label>
            <Text>E-mail</Text>
          </Form.Label>
          {...EmailValidation.messages}
          <Form.Control asChild>
            <TextField.Root
              {...EmailValidation.rules}
              autoComplete="username"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- not applicable
              autoFocus
              onChange={(event) => setEmail(event.target.value)}
              placeholder="alice@company.com"
              type="email"
              value={email}
            />
          </Form.Control>
        </Form.Field>

        <Form.Field name="password" required>
          <Form.Label>
            <Text>Password</Text>
          </Form.Label>
          {...PasswordValidation.messages}
          <Form.Control asChild>
            <TextField.Root
              {...PasswordValidation.rules}
              autoComplete="current-password"
              placeholder="••••••••"
              type="password"
            />
          </Form.Control>
        </Form.Field>

        <Form.Submit>Sign in</Form.Submit>

        {!!routes[Actions.Reset] && (
          <Text align="center" size="1">
            <Link
              href={{
                pathname: routes[Actions.Reset],
                query: pack({
                  organizationId: organization.name,
                  name: email,
                }),
              }}
            >
              Forgot your password?
            </Link>
          </Text>
        )}
      </Form.Root>

      {!!routes[Actions.SingleSignOn] && ssoProvidersList.length > 0 && (
        <>
          <Flex align="center" gap="3" my="5">
            <Separator size="4" />
            <Text color="gray">or</Text>
            <Separator size="4" />
          </Flex>

          <Flex direction="column" gap="4">
            {ssoProvidersList.map((provider) => (
              <Form.Root action={ssoSignIn} key={provider.id}>
                <Form.Field hidden name="organizationId" required>
                  <Form.Control asChild>
                    <input type="hidden" value={organization.name} />
                  </Form.Control>
                </Form.Field>

                <Form.Field hidden name="ssoId" required>
                  <Form.Control asChild>
                    <input type="hidden" value={provider.id} />
                  </Form.Control>
                </Form.Field>

                <Form.Submit>Sign in with {provider.fullname}</Form.Submit>
              </Form.Root>
            ))}
          </Flex>
        </>
      )}
    </>
  )

  /** @param {FormData} formData */
  async function builtinSignIn(formData) {
    const { organizationId, username, password } = Object.fromEntries(formData)

    const signIn = await authenticate('credentials', {
      redirect: false,
      callbackUrl,
      organizationId,
      username,
      password,
    })

    if (signIn.ok) {
      await router.replace(callbackUrl ?? signIn.url ?? '/')
    } else {
      throw Messages[signIn.error] ?? failedLogon
    }
  }

  /** @param {FormData} formData */
  async function ssoSignIn(formData) {
    const { ssoId, organizationId } = /** @type {Record<string, string>} */ (
      Object.fromEntries(formData)
    )

    if (!ssoId) {
      throw new Error(`Invalid provider`)
    }

    await router.push({
      pathname: routes[Actions.SingleSignOn],
      query: {
        ssoId,
        organizationId,
      },
    })
  }
}

/** @type {GetServerSideAuthProps} */
async function getServerSideProps(context, authContext) {
  // Always clear old sso state cookies
  await withSecret(context, 'synqly-sso-state', {
    validate: () => undefined,
  })

  const organizationId = resolveOrganizationId(
    authContext.params.at(0) ?? [context.query.organizationId].flat()[0],
  )

  if (organizationId && !isAllowedOrganizationId(organizationId)) {
    return notFound(context)
  }

  const { organization, ssoProviders, error } =
    await getOrganizationAuth(organizationId)

  if (error) {
    return notFound(context, error?.message)
  }

  return {
    props: {
      organization,
      ssoProviders,
    },
  }
}

const failedLogon = {
  severity: Form.MessageSeverity.ERROR,
  message: 'Logon failed. Please try again.',
}

const Messages = {
  SessionRequired: {
    severity: Form.MessageSeverity.ERROR,
    message:
      'You were automatically logged out. Please sign back in to continue.',
  },
  AccountEnabled: {
    severity: Form.MessageSeverity.INFO,
    message:
      'Your account was successfully enabled. Please sign in to continue.',
  },
  'logon failed': failedLogon,
  'Not Found': failedLogon,
}

/** @param {string} proposedOrganizationId */
function resolveOrganizationId(proposedOrganizationId) {
  const { NEXT_PUBLIC_ALLOWED_ORGS } = process.env
  const allowedOrgs = NEXT_PUBLIC_ALLOWED_ORGS?.split(',') ?? []

  // If only one organization id is allowed return that, otherwise return
  // the proposed organization id
  return allowedOrgs.length === 1 ? allowedOrgs.at(0) : proposedOrganizationId
}

function isAllowedOrganizationId(organizationId) {
  // Get org id allow and deny lists from env variables
  const { NEXT_PUBLIC_ALLOWED_ORGS, NEXT_PUBLIC_BLOCKED_ORGS } = process.env

  const allowedOrgs = NEXT_PUBLIC_ALLOWED_ORGS?.split(',')
  const blockedOrgs = NEXT_PUBLIC_BLOCKED_ORGS?.split(',')
  if (allowedOrgs?.includes(organizationId) === false) {
    return false
  }

  if (blockedOrgs?.includes(organizationId) === true) {
    return false
  }

  return true
}

/** @param {string} organizationId */
async function getOrganizationAuth(organizationId) {
  if (!organizationId) {
    return { organization: null, ssoProviders: null }
  }

  const { MANAGEMENT_TOKEN: token } = process.env

  const [{ result: orgInfo, error }, { result: authConfig }] =
    await Promise.all([
      callAPI(`/v1/organizations/${organizationId}`, { token }),
      callAPI(`/v1/auth/logon/${organizationId}`, { token }),
    ])

  if (error) {
    return new Error(`Unknown organization ${organizationId}`, {
      cause: error,
    })
  }

  const organization = authConfig?.organization ?? orgInfo

  return {
    organization: {
      id: organization.id,
      name: organization.name,
      fullname: organization.fullname,
    },
    ssoProviders:
      authConfig?.sso_providers?.length > 0 &&
      Object.fromEntries(
        authConfig.sso_providers
          .sort(({ fullname: a, fullname: b }) => a.localeCompare(b))
          .map((ssoConfig) => [ssoConfig.id, ssoConfig]),
      ),
  }
}
